import React, { useCallback } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import useFormName from '../../../../Hooks/useFormName';
import { useFormContainerContext } from '../../../../Services/FormContainerContext';
import ErrorMessage from '../ErrorMessage';
import TopFieldContent from '../TopFieldContent';
import useTranslation from '../../../../Hooks/useTranslation';
import CharactersCount from '../CharactersCount/CharactersCount';
import AddressWhisperer from '../../../Base/AddressWhisperer/AddressWhisperer';
import type { AddressConfig } from '../../../Base/AddressWhisperer/AddressWhisperer';

interface AddressWhispererFieldProps {
	name: string;
	label?: string;
	isRequired?: boolean;
	className?: string;
	placeholder?: string;
	charactersLimit?: number;
	showCharCounter?: boolean;
	addressConfig: AddressConfig & { responseMapping?: Record<string, string> };
}

const AddressWhispererField: React.FC<AddressWhispererFieldProps> = ({
	name,
	label,
	isRequired,
	className,
	placeholder,
	charactersLimit,
	showCharCounter = true,
	addressConfig,
}) => {
	const { t } = useTranslation();
	const { fieldName } = useFormName({ name });
	const { name: formContainerName } = useFormContainerContext();
	const { control, setValue } = useFormContext();

	const {
		field,
		fieldState: { error },
	} = useController({
		control,
		name: fieldName,
		defaultValue: '',
	});

	const onAddressSelect = useCallback(
		(item: Record<string, unknown>) => {
			const mapping =
				addressConfig.responseMapping ?? addressConfig.fieldMapping ?? {};
			const fullName = (key: string) =>
				formContainerName ? `${formContainerName}.${key}` : key;
			Object.entries(mapping).forEach(([formField, apiKey]) => {
				if (item[apiKey] != null) {
					setValue(fullName(formField), String(item[apiKey]), {
						shouldValidate: true,
					});
				}
			});
		},
		[addressConfig, formContainerName, setValue],
	);

	return (
		<div className={className}>
			{label && <TopFieldContent label={label} isRequired={isRequired} />}
			<AddressWhisperer
				addressConfig={{ ...addressConfig, onAddressSelect }}
				value={field.value}
				onChange={field.onChange}
				placeholder={placeholder ? t(placeholder) : undefined}
			/>
			{error?.message && <ErrorMessage tx={error.message} />}
			{charactersLimit && showCharCounter && (
				<CharactersCount limit={charactersLimit} value={field.value} />
			)}
		</div>
	);
};

export default AddressWhispererField;
